import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { WysiwygEditorField, Loader } from 'examkrackers-components'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import qs from 'qs'
import { propOr } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { upsertNoteForSubchapterRoutine } from 'modules/books/ducks/actions'
import debounce from 'lodash.debounce'
import { isNotNilOrEmpty } from 'utils/ramda'
import { fetchNotesForSubchapter } from 'services/BooksService'
import {
  getBookByOriginalId,
  getSubchapterById,
  getSubchaptersApiState
} from 'modules/books/ducks/selectors'
import { API_STATES } from 'utils/redux'
import { RootReducer } from 'types'

import eventEmitter from 'providers/eventEmitter'
import events from 'modules/books/utils/events'
import { BOOK_VIEWER_PARAMS } from 'utils/books'

const NOTES_PLACEHOLDER_COLOR = '#949596'

const parseNotesDelta = (notes: Record<string, unknown> | null): unknown => {
  if (notes == null) return null
  try {
    const delta = propOr(null, 'delta_object', notes)
    return delta != null ? JSON.parse(delta as string) : null
  } catch {
    return null
  }
}

const getBookDisplayName = (book: Record<string, unknown> | null | undefined): string => {
  const title = propOr('', 'title', book) as string
  const tag = propOr('', 'tag', book) as string

  if (isNotNilOrEmpty(title)) {
    if (title.includes(' - ')) {
      return title.split(' - ').slice(-1)[0].trim()
    }
    return title
  }

  return tag
}

export const NotesPreview = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const subchaptersApiState = useSelector(getSubchaptersApiState)
  const isChapterDataReady = subchaptersApiState === API_STATES.DONE

  const [notes, setNotes] = useState<Record<string, unknown> | null>(null)
  const lastFetchedSectionIdRef = useRef<string>('')
  const lastPageKeyRef = useRef<string>('')
  const currentSectionIdRef = useRef<string>('')
  const saveInProgressRef = useRef(false)
  const hasUnsavedChangesRef = useRef(false)
  const unsavedChangesSectionIdRef = useRef<string>('')
  const lastEditedContentRef = useRef<{ sectionId: string; delta: unknown } | null>(null)

  const {
    location: { search, pathname }
  } = useHistory()
  const params = useParams<{
    bookId?: string
    chapterOrder?: string
    partOrder?: string
  }>()
  const bookId = propOr('', 'bookId', params) as string
  const chapterOrder = propOr('', 'chapterOrder', params) as string
  const pageKey = `${bookId}-${chapterOrder}-${propOr('', 'partOrder', params)}`
  const parsedQuery = qs.parse(search, { ignoreQueryPrefix: true })

  const currentSectionId = propOr(
    '',
    BOOK_VIEWER_PARAMS.sectionIdContext,
    parsedQuery
  ) as string

  const book = useSelector((state: RootReducer) =>
    getBookByOriginalId(state, bookId)
  )
  const subchapter = useSelector((state: RootReducer) =>
    getSubchapterById(state, currentSectionId)
  )

  const bookDisplayName = getBookDisplayName(book)
  const sectionNumber = `${chapterOrder}.${propOr('', 'order', subchapter)}`
  const notesHeading = t('bookViewer.notes.heading', {
    book: bookDisplayName,
    section: sectionNumber
  })
  const notesPlaceholder = t('bookViewer.notes.placeholder')

  useEffect(() => {
    currentSectionIdRef.current = currentSectionId
  }, [currentSectionId])

  useEffect(() => {
    if (!isChapterDataReady) return
    if (!isNotNilOrEmpty(currentSectionId)) return
    if (lastFetchedSectionIdRef.current === currentSectionId) return

    const sectionIdWeFetch = currentSectionId
    const isPageChange = lastPageKeyRef.current !== pageKey
    if (isPageChange) {
      lastPageKeyRef.current = pageKey
    }

    const runFetch = () => {
      const hadUnsavedOnThisSection =
        hasUnsavedChangesRef.current &&
        unsavedChangesSectionIdRef.current === sectionIdWeFetch

      if (!hadUnsavedOnThisSection) {
        hasUnsavedChangesRef.current = false
      }

      lastFetchedSectionIdRef.current = sectionIdWeFetch

      if (hadUnsavedOnThisSection && lastEditedContentRef.current?.sectionId === sectionIdWeFetch) {
        const cached = lastEditedContentRef.current
        setNotes({
          delta_object: typeof cached.delta === 'string' ? cached.delta : JSON.stringify(cached.delta)
        } as Record<string, unknown>)
      } else {
        setNotes(null)
      }

      eventEmitter.emit(events.notesLoading, true)

      fetchNotesForSubchapter({ sectionId: sectionIdWeFetch })
        .then(resp => {
          const raw = resp?.data?.[0]
          const nextNotes = raw != null ? raw : {}
          if (currentSectionIdRef.current !== sectionIdWeFetch) return
          if (saveInProgressRef.current) return
          if (
            hasUnsavedChangesRef.current &&
            unsavedChangesSectionIdRef.current === sectionIdWeFetch
          )
            return
          setNotes(nextNotes as Record<string, unknown>)
          setTimeout(() => eventEmitter.emit(events.notesLoading, false), 500)
        })
        .catch(() => {
          if (
            currentSectionIdRef.current === sectionIdWeFetch &&
            !saveInProgressRef.current &&
            !(hasUnsavedChangesRef.current && unsavedChangesSectionIdRef.current === sectionIdWeFetch)
          ) {
            setNotes({})
          }
          setTimeout(() => eventEmitter.emit(events.notesLoading, false), 500)
        })
    }

    if (isPageChange) {
      const timerId = setTimeout(runFetch, 1500)
      return () => clearTimeout(timerId)
    }
    runFetch()
    return undefined
  }, [search, currentSectionId, isChapterDataReady, pageKey, pathname])

  const parsedNotes = useMemo(() => parseNotesDelta(notes), [notes])

  const handleSuccess = () => {
    saveInProgressRef.current = false
    hasUnsavedChangesRef.current = false
    setTimeout(() => eventEmitter.emit(events.notesLoading, false), 1000)
    setTimeout(() => eventEmitter.emit(events.notesSavedSuccess, true), 1000)
    setTimeout(() => eventEmitter.emit(events.notesSavedSuccess, false), 2000)
  }

  const handleError = () => {
    saveInProgressRef.current = false
    setTimeout(() => eventEmitter.emit(events.notesLoading, false), 1000)
    setTimeout(() => eventEmitter.emit(events.notesSavedFailure, true), 1000)
    setTimeout(() => eventEmitter.emit(events.notesSavedFailure, false), 2000)
  }

  const handleSaveNote = useCallback(
    payload => dispatch(upsertNoteForSubchapterRoutine(payload)),
    [dispatch]
  )

  const handleOnChange = useCallback(
    (name: string, value: any) => {
      eventEmitter.emit(events.notesLoading, true)

      const delta_object = value.getContents()
      const raw = value.getText()

      const payload = {
        values: {
          delta_object,
          raw,
          sectionId: currentSectionId
        },
        callback: handleSuccess,
        onFailure: handleError
      }

      handleSaveNote(payload)
    },
    [currentSectionId, handleSaveNote]
  )

  const handleOnChangeRef = useRef(handleOnChange)
  handleOnChangeRef.current = handleOnChange

  const debounceHandlerRef = useRef(
    debounce((name: string, value: any) => handleOnChangeRef.current(name, value), 300)
  )

  const handleEditorChange = useCallback((name: string, value: any) => {
    hasUnsavedChangesRef.current = true
    saveInProgressRef.current = true
    unsavedChangesSectionIdRef.current = currentSectionId
    try {
      const delta = value?.getContents?.() ?? value
      lastEditedContentRef.current = { sectionId: currentSectionId, delta }
    } catch {
      /* ignore */
    }
    debounceHandlerRef.current(name, value)
  }, [currentSectionId])

  useEffect(() => {
    if (!isChapterDataReady) return

    let cancelled = false
    let retryId: number | undefined

    const syncNotesChrome = () => {
      if (cancelled) return

      const container = document.querySelector(
        '#create-subchapter-note .ql-container'
      ) as HTMLElement | null
      const editorRoot = document.querySelector(
        '#create-subchapter-note .ql-editor'
      ) as HTMLElement | null

      if (!container || !editorRoot) {
        retryId = window.setTimeout(syncNotesChrome, 50)
        return
      }

      editorRoot.setAttribute('data-placeholder', notesPlaceholder)

      let headingEl = container.querySelector(
        '[data-notes-heading]'
      ) as HTMLElement | null

      if (!headingEl) {
        headingEl = document.createElement('div')
        headingEl.setAttribute('data-notes-heading', 'true')
        headingEl.setAttribute('aria-live', 'polite')
        container.insertBefore(headingEl, editorRoot)
      }

      headingEl.textContent = notesHeading
    }

    syncNotesChrome()

    return () => {
      cancelled = true
      if (retryId != null) {
        window.clearTimeout(retryId)
      }
    }
  }, [
    notesHeading,
    notesPlaceholder,
    currentSectionId,
    parsedNotes,
    isChapterDataReady
  ])

  if (!isChapterDataReady) {
    return (
      <EditorWrapper>
        <NotesLoadingPlaceholder>
          <Loader />
        </NotesLoadingPlaceholder>
      </EditorWrapper>
    )
  }

  return (
    <EditorWrapper>
      <WysiwygEditorField
        key={`note-editor-${currentSectionId}`}
        id='create-subchapter-note'
        t={t}
        name='delta_object'
        onChange={handleEditorChange}
        validate={() => true}
        initialValue={parsedNotes}
        formats={{
          size: true,
          header: true,
          bold: true,
          italic: true,
          underline: true,
          orderedList: true,
          bulletList: true,
          align: true,
          scriptSuper: true,
          scriptSub: true,
          blockquote: true,
          formula: true,
          clean: true
        }}
      />
    </EditorWrapper>
  )
}

export default NotesPreview

const EditorWrapper = styled.form`
  border-radius: 8px;
  overflow: hidden;
  height: 100%;

  #create-subchapter-note {
    flex-grow: 1;
  }

  & > div {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .text-editor-container {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ql-toolbar.ql-snow {
    flex-shrink: 0;
  }

  .ql-container.ql-snow {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  [data-notes-heading] {
    flex-shrink: 0;
    margin: 0;
    padding: 12px 15px 4px;
    font-family: Arial, sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    color: #1a1a1a;
  }

  .ql-editor {
    flex: 1;
    font-size: 14px;
    line-height: 1.4;
  }

  .ql-editor.ql-blank::before {
    color: ${NOTES_PLACEHOLDER_COLOR};
    font-size: 18px;
    font-style: normal;
    left: 15px;
    right: 15px;
    content: attr(data-placeholder);
  }

  .ql-editor.ql-blank:focus::before {
    content: none;
  }
`

const NotesLoadingPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 120px;
`

